'use client';

import IconButton from '@/components/Redactie/IconButton/IconButton';
import Dropdown from '@/components/Redactie/Dropdown/Dropdown';
import {
    mdiArrowDownCircle,
    mdiArrowDownRight,
    mdiArrowRightBottom,
    mdiArrowUpCircle,
    mdiDelete,
    mdiCircle,
    mdiPlusThick,
    mdiChevronUp,
    mdiAlert,
} from '@mdi/js';
import DropdownSeparator from '@/components/Redactie/Dropdown/DropdownSeparator';
import DropdownButton from '@/components/Redactie/Dropdown/DropdownButton';
import Icon from '@mdi/react';
import {
    ComponentType,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { EditorBlock } from '@/components/Redactie/Page/PagesEditor';
import { PageBlock } from '@/hooks/usePage';
import { Character } from '@/resources/Characters';
import { BLOCK_TYPES } from './BlockTypes';
import { isEqual } from '@/utils/any';

export enum BlockTransitionType {
    INSTANT,
    AUTOMATIC,
    INTERACTIVE,
    SKIPPED,
}

export const BlockTransitionTypeDisplayNames = {
    [BlockTransitionType.INSTANT]: 'Samen met interactie',
    [BlockTransitionType.AUTOMATIC]: 'Automatische transitie',
    [BlockTransitionType.INTERACTIVE]: 'Interactieve transitie',
    [BlockTransitionType.SKIPPED]: 'Overgeslagen',
} as const;

export interface BlockType {
    type: string;
    displayName: string;
    icon: string;
    transitionType: BlockTransitionType;
    component: React.FC<BlockProps> | ComponentType<BlockProps>;
    isEditorOnly: boolean;
    preload?: (block: PageBlock) => void;
}

export interface BlockProps {
    block?: PageBlock;
    subjectId: number;
    chapterId: number;
    pageId: number;
    id?: number;
    updatedAt: string;
    type: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>;
    order: number;
    uuid: EditorBlock['uuid'];
    media: PageBlock['media'];
    attachments: EditorBlock['attachments'];
    isEditable?: boolean;
    isEditorPreview?: boolean;
    isAnimated?: boolean;
    isFirst?: boolean;
    isFirstNonEditorOnlyBlock?: boolean;
    isLast?: boolean;
    activeChatCharacter?: Character;
    addNewBlockComponent?: (
        order: number,
        close: () => void,
    ) => React.ReactNode;
    nextBlockTransitionType: BlockTransitionType;
    onContentHeightChanged?: () => void;
    onFinished?: () => void;
    onEdited?: (block: {
        uuid: EditorBlock['uuid'];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: Record<string, any>;
        attachments: EditorBlock['attachments'];
    }) => void;
    onDeleted?: () => void;
    onMoved?: (direction: 1 | -1) => void;
}

const Block = ({
    block,
    subjectId,
    chapterId,
    pageId,
    id,
    updatedAt,
    type,
    data,
    order,
    uuid,
    media,
    attachments,
    isEditable = false,
    isEditorPreview = false,
    isAnimated = true,
    isFirst,
    isFirstNonEditorOnlyBlock,
    isLast,
    activeChatCharacter,
    addNewBlockComponent,
    nextBlockTransitionType,
    onContentHeightChanged,
    onFinished,
    onEdited,
    onDeleted,
    onMoved,
}: BlockProps) => {
    const [hasFinished, setHasFinished] = useState<boolean>(false);
    const BlockComponent = BLOCK_TYPES[type]?.component ?? BlockError;

    useEffect(() => {
        setHasFinished(false);
    }, [uuid, isEditable, isAnimated]);

    const finish = useCallback(() => {
        // Block finishing if already finished
        if (hasFinished === true) return;

        onFinished?.();
        setHasFinished(true);
    }, [hasFinished, onFinished, setHasFinished]);

    const blockComponent = (
        <BlockComponent
            block={block}
            subjectId={subjectId}
            chapterId={chapterId}
            pageId={pageId}
            id={id}
            updatedAt={updatedAt}
            type={type}
            data={data}
            order={order}
            uuid={uuid}
            media={media}
            attachments={attachments}
            isEditable={isEditable}
            isEditorPreview={isEditorPreview}
            isAnimated={isAnimated}
            isFirstNonEditorOnlyBlock={isFirstNonEditorOnlyBlock}
            activeChatCharacter={activeChatCharacter}
            nextBlockTransitionType={nextBlockTransitionType}
            onContentHeightChanged={onContentHeightChanged}
            onFinished={!hasFinished ? finish : undefined}
            onEdited={onEdited}
            onDeleted={onDeleted}
            onMoved={onMoved}
        />
    );

    if (!isEditable) return blockComponent;
    else
        return (
            <BlockEditorWrapper
                order={order}
                block={block}
                blockType={BLOCK_TYPES[type]}
                onDeleted={onDeleted}
                onMoved={onMoved}
                isFirst={isFirst}
                isLast={isLast}
                addNewBlockComponent={addNewBlockComponent}
                transitionType={BLOCK_TYPES[type]?.transitionType}
                nextBlockTransitionType={nextBlockTransitionType}
            >
                {blockComponent}
            </BlockEditorWrapper>
        );
};

export interface BlockEditorWrapperProps {
    order: number;
    block?: PageBlock;
    blockType?: BlockType;
    isFirst?: boolean;
    isLast?: boolean;
    addNewBlockComponent?: (
        order: number,
        close: () => void,
    ) => React.ReactNode;
    onDeleted?: () => void;
    onMoved?: (direction: 1 | -1) => void;
    transitionType?: BlockTransitionType;
    nextBlockTransitionType: BlockTransitionType;
    children: React.ReactNode;
}

const BlockEditorWrapper = ({
    order,
    block,
    blockType,
    isFirst,
    isLast,
    addNewBlockComponent,
    onDeleted,
    onMoved,
    transitionType,
    nextBlockTransitionType,
    children,
}: BlockEditorWrapperProps) => {
    const originalBlock = useRef<PageBlock | undefined>(block);
    const isEdited = useMemo(() => {
        return isBlockEdited(block, originalBlock.current);
    }, [block]);
    const [
        isNewBlockComponentShown,
        setIsNewBlockComponentShown,
    ] = useState<boolean>(false);

    return (
        <div className="group relative w-full rounded-xl px-4 pt-2 transition-all hover:bg-white hover:shadow-md focus-within:bg-white focus-within:shadow-md focus-within:my-1">
            <div className="flex items-center justify-center gap-1 mb-1 font-bold opacity-50 group-hover:opacity-100 group-focus-within:opacity-100 scale-90 group-hover:scale-100 group-focus-within:scale-100 transition-all duration-500">
                <Icon size={0.75} path={blockType?.icon ?? mdiAlert} />
                {blockType?.displayName ?? 'Onbekend'}
            </div>
            {isEdited && (
                <Icon
                    size={0.5}
                    path={mdiCircle}
                    className="absolute top-4 right-4 text-green-500"
                    aria-label="Blokje is aangepast"
                />
            )}
            {children}
            <div className="relative mt-3 mb-1 flex w-full scale-90 items-center justify-center gap-2 opacity-50 transition-all group-focus-within:z-1 group-focus-within:mb-3 group-focus-within:scale-100 group-focus-within:opacity-100 group-hover:mb-3 group-hover:scale-100 group-hover:opacity-100 duration-500">
                <p className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-black text-opacity-75">
                    <Icon
                        size={0.5}
                        path={mdiArrowRightBottom}
                        className="self-start"
                    />
                    {!blockType?.isEditorOnly && (
                        <span>
                            {nextBlockTransitionType ==
                            BlockTransitionType.INTERACTIVE
                                ? BlockTransitionTypeDisplayNames[
                                      BlockTransitionType.INSTANT
                                  ]
                                : BlockTransitionTypeDisplayNames[
                                      transitionType ??
                                          BlockTransitionType.SKIPPED
                                  ]}
                        </span>
                    )}
                    {blockType?.isEditorOnly && <span>Overgeslagen</span>}
                    <Icon
                        size={0.5}
                        path={mdiArrowDownRight}
                        className="self-end"
                    />
                </p>
                <div className="flex gap-2 items-center">
                    <IconButton
                        isMini
                        icon={mdiArrowUpCircle}
                        onClick={() => onMoved?.(-1)}
                        label="Omhoog"
                        enabled={!isFirst}
                        className="shrink-0"
                    />
                    <IconButton
                        isMini
                        icon={mdiArrowDownCircle}
                        onClick={() => onMoved?.(1)}
                        label="Omlaag"
                        enabled={!isLast}
                        className="shrink-0"
                    />
                    <IconButton
                        isMini
                        icon={mdiPlusThick}
                        onClick={() =>
                            setIsNewBlockComponentShown(
                                !isNewBlockComponentShown,
                            )
                        }
                        label="Nieuw blokje hierna"
                        enabled={!isLast}
                        className="shrink-0"
                    />
                    <Dropdown
                        className="shrink-0"
                        contentContainerClassName="w-48"
                        customAlignmentClassName="-translate-x-1/2 -translate-y-3/4"
                        triggerElement={
                            <IconButton
                                isMini
                                icon={mdiDelete}
                                label="Verwijderen"
                            />
                        }
                    >
                        <DropdownButton
                            className="text-red-500"
                            onClick={onDeleted}
                        >
                            Blokje verwijderen
                        </DropdownButton>
                        <DropdownSeparator />
                        <DropdownButton className="text-green-500">
                            Annuleren
                        </DropdownButton>
                    </Dropdown>
                </div>
            </div>

            {isNewBlockComponentShown && addNewBlockComponent && (
                <div className="bg-slate-100 rounded-lg p-2 pb-0.5 mb-4">
                    {addNewBlockComponent(order, () => {
                        setIsNewBlockComponentShown(false);
                    })}
                    <div className="flex justify-center my-2">
                        <IconButton
                            label="Annuleren"
                            icon={mdiChevronUp}
                            onClick={() => setIsNewBlockComponentShown(false)}
                        >
                            Annuleren
                        </IconButton>
                    </div>
                </div>
            )}
        </div>
    );
};

const BlockError = ({ block }: { block: PageBlock | undefined }) => (
    <div>
        Error: geen blokje van dit type gevonden.
        <code>{JSON.stringify(block, null, 2)}</code>
    </div>
);

export const isBlockEdited = (
    editorBlock?: EditorBlock | PageBlock,
    originalBlock?: PageBlock,
    enableLogs = false,
) => {
    if (!editorBlock) return true;

    // If the block is new, it's automatically edited
    if (editorBlock.id === undefined) {
        if (enableLogs) console.log('\t\tblock is added');
        return true;
    }

    // Else check for new attachments
    if (
        'attachments' in editorBlock &&
        Object.keys(editorBlock.attachments).length > 0
    ) {
        if (enableLogs) console.log('\t\tblock has new attachment');
        return true;
    }

    // Else get original block to check for changes in data or order
    const isChanged =
        !isEqual(originalBlock?.data, editorBlock.data) ||
        originalBlock?.order !== editorBlock.order;

    // Check data and order
    if (enableLogs && isChanged) {
        console.log(
            '\t\tblock data is changed',
            originalBlock?.data,
            editorBlock.data,
        );
    }
    return isChanged;
};

export default Block;
